import React from "react";
import styled from "styled-components";
import { FiMapPin } from "react-icons/fi";
import { FiCalendar } from "react-icons/fi";
import { HiOutlineCollection } from "react-icons/hi";
import { FiEye } from "react-icons/fi";
import { Box, Flex } from "rebass/styled-components";
import { Title3, Body } from "../../../../Components/Text";
import { LoadingSkeleton } from "../../../../Components/Skeloton";
import { DividerAlt } from "../../../../../components/atoms/dividerAlt";
import { Button } from "../../../../../components/atoms/form/button";

import { formatNumber } from "../../../../../components/molecules/clip/utils";
import { Link } from "../../../../../components/atoms/link";
import { useTranslation } from "react-i18next";
import { ReactComponent as XIcon } from "../../../../Assets/Socials/X.svg";
import { ReactComponent as YoutubeIcon } from "../../../../Assets/Socials/YouTube.svg";
import { ReactComponent as InstagramIcon } from "../../../../Assets/Socials/Instagram.svg";
import { ReactComponent as TikTokIcon } from "../../../../Assets/Socials/TikTok.svg";
import { ReactComponent as TwitchIcon } from "../../../../Assets/Socials/Twitch.svg";
import { ReactComponent as SteamIcon } from "../../../../Assets/Socials/Steam.svg";

import { getUserSocials } from "../../../../Utilities/User";

export function ProfileSideRailUserInfo({
  country,
  joinDate,
  stats,
  socials,
  isOwnProfile,
}) {
  const createDate = new Date(parseInt(joinDate));
  const month = monthNames[createDate.getMonth()];
  const year = createDate.getFullYear();
  const socialLinks = getUserSocials({ socials: socials });
  const hasSocialLinks = Object.keys(socialLinks).length;
  const { t } = useTranslation("translation", {
    keyPrefix: "profile.userStats",
  });

  return (
    <StyledBox variant="sidebar.new">
      <StyledTitle3 mb={3}> {t("about")} </StyledTitle3>
      <DividerAlt />
      <Flex mb={5}>
        <Box size="22px" as={FiMapPin} mr={2} color="envy" />{" "}
        <Body color="ice" ml={4} name="profile-user-country">
          {country}
        </Body>
      </Flex>
      <Flex mb={5}>
        <Box size="22px" as={FiCalendar} mr={2} color="envy" />
        <Body color="ice" ml={4}>
          {t("dateJoined")} {`${month} ${year}`}
        </Body>
      </Flex>
      <Flex mb={5}>
        <Box size="22px" as={HiOutlineCollection} mr={2} color="envy" />{" "}
        <Body color="ice" ml={4}>
          {formatNumber(stats?.totalClips || 0)} {t("clips")}
        </Body>
      </Flex>
      {!!stats?.totalViews && (
        <Flex mb={2}>
          <Box size="22px" as={FiEye} mr={2} color="envy" />{" "}
          <Body color="ice" ml={4}>
            {formatNumber(stats?.totalViews || 0)} {t("views")}
          </Body>
        </Flex>
      )}
      {isOwnProfile || hasSocialLinks ? (
        <SocialLinks
          socialLinks={socialLinks}
          hasSocialLinks={hasSocialLinks}
        />
      ) : (
        <></>
      )}
    </StyledBox>
  );
}

const SocialLinks = ({ socialLinks, hasSocialLinks }) => (
  <>
    <StyledTitle3 mb={3} mt={6}>
      Social Profiles
    </StyledTitle3>
    <DividerAlt />
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      style={{ gap: 20 }}
    >
      {hasSocialLinks ? (
        <>
          {socialLinks.youtube ? (
            <SocialLink href={socialLinks.youtube.externalLink} target="_blank">
              <YoutubeIcon style={iconStyles} />
              {socialLinks.youtube.socialDisplayName}
            </SocialLink>
          ) : null}
          {socialLinks.twitch ? (
            <SocialLink href={socialLinks.twitch.externalLink} target="_blank">
              <TwitchIcon size={22} style={iconStyles} />
              {socialLinks.twitch.socialDisplayName}
            </SocialLink>
          ) : null}
          {socialLinks.tiktok ? (
            <SocialLink href={socialLinks.tiktok.externalLink} target="_blank">
              <TikTokIcon size={22} style={iconStyles} />
              {socialLinks.tiktok.socialDisplayName}
            </SocialLink>
          ) : null}
          {socialLinks.twitter ? (
            <SocialLink href={socialLinks.twitter.externalLink} target="_blank">
              <XIcon style={iconStyles} />
              {socialLinks.twitter.socialDisplayName}
            </SocialLink>
          ) : null}
          {socialLinks.instagram ? (
            <SocialLink
              href={socialLinks.instagram.externalLink}
              target="_blank"
            >
              <InstagramIcon style={iconStyles} />
              {socialLinks.instagram.socialDisplayName}
            </SocialLink>
          ) : null}
          {socialLinks.steam ? (
            <SocialLink href={socialLinks.steam.externalLink} target="_blank">
              <SteamIcon size={22} style={iconStyles} />
              {socialLinks.steam.socialDisplayName}
            </SocialLink>
          ) : null}
        </>
      ) : (
        <Button
          size="small"
          classification="secondary"
          flex={true}
          to="/accountsettings"
        >
          Add your Social Profiles
        </Button>
      )}
    </Flex>
  </>
);

const LoadingUserStats = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "profile.userStats",
  });
  return (
    <StyledBox variant="sidebar.dark">
      <StyledTitle3 mb={3}> {t("about")} </StyledTitle3>
      <DividerAlt />
      <Flex mb={5}>
        <Box size="22px" as={FiMapPin} mr={2} color="envy" />{" "}
        <Body color="ice" ml={4}>
          <LoadingSkeleton height={16} width={60} />
        </Body>
      </Flex>
      <Flex mb={5}>
        <Box size="22px" as={FiCalendar} mr={2} color="envy" />{" "}
        <LoadingSkeleton height={16} width={60} />
      </Flex>

      <Flex mb={5}>
        <Box size="22px" as={HiOutlineCollection} mr={2} color="envy" />{" "}
        <Body color="ice" ml={4}>
          <LoadingSkeleton height={16} width={60} />
        </Body>
      </Flex>
      <Flex>
        <Box size="22px" as={FiEye} mr={2} color="envy" />{" "}
        <Body color="ice" ml={4}>
          <LoadingSkeleton height={16} width={60} />
        </Body>
      </Flex>
    </StyledBox>
  );
};

const StyledTitle3 = styled(Title3)`
  font-weight: 600;
`;

const StyledBox = styled(Box)`
  width: 100%;
`;

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const iconStyles = {
  display: "inline-block",
  verticalAlign: "middle",
  marginRight: "20px",
};
const SocialLink = styled(Link).attrs(() => ({
  variant: "link.profileSocial",
}))`
  &:not(:last-child) {
    margin-right: 15px;
  }
`;
